<div class="ks-container ks-users">
  <header class="ks-page-header ks-rise-1">
    <div>
      <h1 class="ks-page-title">Usuarios</h1>
      <p class="ks-page-subtitle">
        Directorio de las cuentas de KeyStoreMan. Las altas ocurren en la app móvil; desde aquí se
        consultan y se editan. Incluye cuentas de la versión anterior que aún viven solo en
        Firebase Auth y todavía no se migran a la base de datos.
      </p>
    </div>

    @if (loaded() && !loading() && !error()) {
      <p class="ks-users__count ks-nums" aria-live="polite">
        <strong>{{ total() }}</strong>
        {{ total() === 1 ? 'usuario' : 'usuarios' }}
      </p>
    }
  </header>

  <div class="ks-filter-bar ks-rise-2" role="search">
    <div class="ks-filter-bar__controls">
      <label class="visually-hidden" for="ks-users-search">Buscar usuario</label>
      <div class="ks-users__search">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input
          id="ks-users-search"
          type="search"
          class="ks-field"
          placeholder="Buscar por nombre, apellidos o correo"
          autocomplete="off"
          [value]="search()"
          (input)="onSearch($any($event.target).value)"
        />
      </div>

      <!--
        ng-select, no <select>: todos los desplegables de la consola deben
        permitir BUSCAR. El id del input interno lo pone `labelForId`, asi que el
        `<label for>` de arriba sigue apuntando al elemento que recibe el foco.
        "Todos los perfiles"/"Todos los estados" quedan como item REAL de la
        lista (no como placeholder) para no cambiar el comportamiento del select
        nativo que reemplazan.
      -->
      <div class="ks-users__profile-filter">
        <label class="visually-hidden" for="ks-users-profile">Filtrar por perfil</label>
        <ng-select
          labelForId="ks-users-profile"
          [items]="profileOptions()"
          bindLabel="label"
          bindValue="value"
          [ngModel]="profileFilter()"
          (change)="onProfileFilter($event)"
          placeholder="Todos los perfiles"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando perfiles…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>

      <div class="ks-users__status-filter">
        <label class="visually-hidden" for="ks-users-status">Filtrar por estado</label>
        <ng-select
          labelForId="ks-users-status"
          [items]="statusOptions"
          bindLabel="label"
          bindValue="value"
          [ngModel]="statusFilter()"
          (change)="onStatusFilter($event)"
          placeholder="Todos los estados"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>

      <div class="ks-users__registered-filter">
        <label class="visually-hidden" for="ks-users-registered">Filtrar por origen</label>
        <ng-select
          labelForId="ks-users-registered"
          [items]="registeredOptions"
          bindLabel="label"
          bindValue="value"
          [ngModel]="registeredFilter()"
          (change)="onRegisteredFilter($event)"
          placeholder="Todos los orígenes"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>
    </div>

    <div class="ks-filter-bar__actions">
      <button
        type="button"
        class="ks-btn ks-btn--ghost ks-btn--sm"
        [disabled]="!hasFilters()"
        (click)="clearFilters()"
      >
        <i class="bi bi-x-circle" aria-hidden="true"></i>
        Limpiar filtros
      </button>
    </div>
  </div>

  <section class="ks-panel ks-rise-3" aria-labelledby="ks-users-heading">
    <h2 class="visually-hidden" id="ks-users-heading">Listado de usuarios</h2>

    <!-- Orden obligatorio: carga -> error -> vacio -> listado. El estado vacio
         solo puede aparecer cuando ya se sabe con certeza que no hay datos.
         El esqueleto debe corresponder a la vista que se va a mostrar (filas
         o tarjetas): si no, hay salto de layout al terminar de cargar. -->
    @if (!loaded() || loading()) {
      @if (!isMobile()) {
        <div class="ks-users__skeleton">
          <app-skeleton [count]="8" height="44px" label="Cargando usuarios" />
        </div>
      } @else {
        <ul class="ks-users__cards">
          @for (item of skeletonCards; track item) {
            <li class="ks-users__card">
              <div class="ks-skeleton-stack">
                <app-skeleton shape="line" width="60%" height="16px" label="Cargando usuarios" />
                <app-skeleton shape="line" width="40%" height="12px" />
                <app-skeleton shape="line" width="70%" height="12px" />
              </div>
            </li>
          }
        </ul>
      }
    } @else if (error()) {
      <app-empty-state
        icon="wifi-off"
        title="No se pudieron cargar los usuarios"
        [message]="error()!"
      >
        <button type="button" class="ks-btn ks-btn--glass" (click)="load()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    } @else if (users().length === 0) {
      @if (hasFilters()) {
        <app-empty-state
          icon="search"
          title="Sin coincidencias"
          message="Ningún usuario coincide con los filtros aplicados. Prueba con otro término de búsqueda."
        >
          <button type="button" class="ks-btn ks-btn--glass" (click)="clearFilters()">
            <i class="bi bi-x-circle" aria-hidden="true"></i>
            Limpiar filtros
          </button>
        </app-empty-state>
      } @else {
        <app-empty-state
          icon="people"
          title="Todavía no hay usuarios"
          message="Las cuentas se crean desde la app móvil de KeyStoreMan. En cuanto exista la primera, aparecerá aquí."
        />
      }
    } @else {
      @if (!isMobile()) {
      <div class="ks-table-wrap">
        <table class="ks-table ks-table--sticky-first">
          <caption class="visually-hidden">
            Usuarios ordenados por fecha de registro, del más reciente al más antiguo. Incluye
            cuentas de Firebase Auth que aún no se han migrado a la base de datos: esas filas no
            tienen ficha de detalle ni se pueden editar.
          </caption>

          <thead>
            <tr>
              <th scope="col">Usuario</th>
              <th scope="col">Origen</th>
              <th scope="col">Perfil</th>
              <th scope="col">Estado</th>
              <th scope="col">Aceptación legal</th>
              <th scope="col">Último acceso</th>
              <th scope="col">Registro</th>
              <th scope="col" class="ks-users__th-actions">Acciones</th>
            </tr>
          </thead>

          <tbody>
            @for (user of users(); track user.uid) {
              <tr [appRowLink]="user.registered ? ['/dashboard/users', user.uid] : null">
                <td>
                  <!-- Sin registro en BD no hay ficha de detalle: el nombre
                       queda como texto plano, no como enlace a una ruta que
                       fallaría. -->
                  @if (user.registered) {
                    <a class="ks-users__name" [routerLink]="['/dashboard/users', user.uid]">
                      {{ user.fullName }}
                    </a>
                  } @else {
                    <span class="ks-users__name ks-users__name--plain">{{ user.fullName }}</span>
                  }
                  <span class="ks-users__email">{{ user.email ?? 'Sin correo' }}</span>
                </td>

                <td>
                  <span
                    class="ks-chip ks-users__origin"
                    [class.ks-users__origin--pending]="!user.registered"
                    [title]="
                      user.registered
                        ? 'Ya tiene registro propio en la base de datos.'
                        : 'Existe en Firebase Auth pero todavía no se ha migrado a la base de datos.'
                    "
                  >
                    <i
                      class="bi"
                      [class.bi-database-check]="user.registered"
                      [class.bi-cloud-arrow-up]="!user.registered"
                      aria-hidden="true"
                    ></i>
                    {{ user.registered ? 'En base de datos' : 'Pendiente de migrar' }}
                  </span>
                </td>

                <td>
                  @if (user.registered) {
                    <span class="ks-chip" [class.ks-chip--brand]="user.isSudo">
                      <i
                        class="bi"
                        [class.bi-shield-lock-fill]="user.isSudo"
                        [class.bi-person]="!user.isSudo"
                        aria-hidden="true"
                      ></i>
                      {{ user.profile }}
                    </span>
                  } @else {
                    <span class="ks-muted">—</span>
                  }
                </td>

                <td>
                  @if (user.registered) {
                    <span
                      class="ks-badge"
                      [class]="user.status ? 'ks-badge--success' : 'ks-badge--muted'"
                    >
                      {{ user.status ? 'Activo' : 'Inactivo' }}
                    </span>
                  } @else {
                    <span class="ks-muted">—</span>
                  }
                </td>

                <td>
                  @if (user.registered) {
                    <span
                      class="ks-badge"
                      [class]="legalStatusBadgeClass(user)"
                      [title]="legalStatusTitle(user)"
                    >
                      {{ legalStatusLabel(user) }}
                    </span>
                  } @else {
                    <span class="ks-muted" [title]="legalStatusTitle(user)">—</span>
                  }
                </td>

                <td>
                  @if (!user.registered) {
                    <span class="ks-muted">—</span>
                  } @else if (user.lastLoginAt) {
                    {{ user.lastLoginAt | dateToMoment }}
                  } @else {
                    <span class="ks-muted">Nunca</span>
                  }
                </td>

                <td>
                  @if (user.registered && user.createdAt) {
                    {{ user.createdAt | dateToMoment: 'DD/MM/YYYY' }}
                  } @else {
                    <span class="ks-muted">—</span>
                  }
                </td>

                <td>
                  <div class="ks-table__actions">
                    @if (user.registered) {
                      <a
                        class="ks-icon-btn"
                        [routerLink]="['/dashboard/users', user.uid]"
                        [attr.aria-label]="'Ver el detalle de ' + user.fullName"
                      >
                        <i class="bi bi-eye" aria-hidden="true"></i>
                      </a>

                      <button
                        type="button"
                        class="ks-icon-btn"
                        [attr.aria-label]="'Editar a ' + user.fullName"
                        (click)="edit(user)"
                      >
                        <i class="bi bi-pencil" aria-hidden="true"></i>
                      </button>
                    } @else {
                      <button
                        type="button"
                        class="ks-icon-btn"
                        disabled
                        [attr.aria-label]="
                          'Ver detalle no disponible: ' + user.fullName + ' aún no está registrado en la base de datos'
                        "
                        [title]="'Sin ficha de detalle: aún no está registrado en la base de datos'"
                      >
                        <i class="bi bi-eye" aria-hidden="true"></i>
                      </button>

                      <button
                        type="button"
                        class="ks-icon-btn"
                        disabled
                        [attr.aria-label]="
                          'Editar no disponible: ' + user.fullName + ' aún no está registrado en la base de datos'
                        "
                        [title]="'Sin nada que editar: aún no está registrado en la base de datos'"
                      >
                        <i class="bi bi-pencil" aria-hidden="true"></i>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      } @else {
        <!-- Enlace real en el nombre: sigue siendo el único camino por
             teclado. `RowLinkDirective` en el `<li>` es solo un atajo de
             ratón/táctil, igual que en la fila de la tabla. -->
        <ul class="ks-users__cards">
          @for (user of users(); track user.uid) {
            <li
              class="ks-users__card"
              [class.ks-users__card--pending]="!user.registered"
              [appRowLink]="user.registered ? ['/dashboard/users', user.uid] : null"
            >
              <div class="ks-users__card-head">
                <div class="ks-users__identity-body">
                  <!-- Sin registro en BD no hay ficha de detalle: el nombre
                       queda como texto plano, no como enlace roto. -->
                  @if (user.registered) {
                    <a class="ks-users__name" [routerLink]="['/dashboard/users', user.uid]">
                      {{ user.fullName }}
                    </a>
                  } @else {
                    <span class="ks-users__name ks-users__name--plain">{{ user.fullName }}</span>
                  }
                  <span class="ks-users__email">{{ user.email ?? 'Sin correo' }}</span>
                </div>

                @if (user.registered) {
                  <span
                    class="ks-badge"
                    [class]="user.status ? 'ks-badge--success' : 'ks-badge--muted'"
                  >
                    {{ user.status ? 'Activo' : 'Inactivo' }}
                  </span>
                } @else {
                  <span class="ks-muted" title="Sin estado: aún no está registrado en la base de datos">—</span>
                }
              </div>

              <div class="ks-users__card-meta">
                <span
                  class="ks-chip ks-users__origin"
                  [class.ks-users__origin--pending]="!user.registered"
                  [title]="
                    user.registered
                      ? 'Ya tiene registro propio en la base de datos.'
                      : 'Existe en Firebase Auth pero todavía no se ha migrado a la base de datos.'
                  "
                >
                  <i
                    class="bi"
                    [class.bi-database-check]="user.registered"
                    [class.bi-cloud-arrow-up]="!user.registered"
                    aria-hidden="true"
                  ></i>
                  {{ user.registered ? 'En base de datos' : 'Pendiente de migrar' }}
                </span>

                @if (user.registered && user.profile) {
                  <span class="ks-chip" [class.ks-chip--brand]="user.isSudo">
                    <i
                      class="bi"
                      [class.bi-shield-lock-fill]="user.isSudo"
                      [class.bi-person]="!user.isSudo"
                      aria-hidden="true"
                    ></i>
                    {{ user.profile }}
                  </span>
                } @else {
                  <span class="ks-muted" title="Sin perfil: aún no está registrado en la base de datos">—</span>
                }

                @if (user.registered) {
                  <span
                    class="ks-badge"
                    [class]="legalStatusBadgeClass(user)"
                    [title]="legalStatusTitle(user)"
                  >
                    {{ legalStatusLabel(user) }}
                  </span>
                } @else {
                  <span class="ks-muted" [title]="legalStatusTitle(user)">—</span>
                }
              </div>

              <!-- Bloque SIEMPRE presente, con o sin registro: una raya en
                   vez de omitir la fecha, para que no parezca que faltó por
                   cargar (mismo criterio que la tabla de escritorio). -->
              <div class="ks-users__card-dates ks-muted">
                @if (!user.registered) {
                  <span>Último acceso: —</span>
                  <span>Registro: —</span>
                } @else {
                  @if (user.lastLoginAt) {
                    <span>Último acceso: {{ user.lastLoginAt | dateToMoment }}</span>
                  } @else {
                    <span>Nunca ha iniciado sesión</span>
                  }
                  @if (user.createdAt) {
                    <span>Registro: {{ user.createdAt | dateToMoment: 'DD/MM/YYYY' }}</span>
                  }
                }
              </div>

              <div class="ks-table__actions">
                @if (user.registered) {
                  <a
                    class="ks-icon-btn"
                    [routerLink]="['/dashboard/users', user.uid]"
                    [attr.aria-label]="'Ver el detalle de ' + user.fullName"
                  >
                    <i class="bi bi-eye" aria-hidden="true"></i>
                  </a>

                  <button
                    type="button"
                    class="ks-icon-btn"
                    [attr.aria-label]="'Editar a ' + user.fullName"
                    (click)="edit(user)"
                  >
                    <i class="bi bi-pencil" aria-hidden="true"></i>
                  </button>
                } @else {
                  <button
                    type="button"
                    class="ks-icon-btn"
                    disabled
                    [attr.aria-label]="
                      'Ver detalle no disponible: ' + user.fullName + ' aún no está registrado en la base de datos'
                    "
                    [title]="'Sin ficha de detalle: aún no está registrado en la base de datos'"
                  >
                    <i class="bi bi-eye" aria-hidden="true"></i>
                  </button>

                  <button
                    type="button"
                    class="ks-icon-btn"
                    disabled
                    [attr.aria-label]="
                      'Editar no disponible: ' + user.fullName + ' aún no está registrado en la base de datos'
                    "
                    [title]="'Sin nada que editar: aún no está registrado en la base de datos'"
                  >
                    <i class="bi bi-pencil" aria-hidden="true"></i>
                  </button>
                }
              </div>
            </li>
          }
        </ul>
      }

      <app-pagination
        [total]="total()"
        [page]="page()"
        [pageSize]="pageSize()"
        (pageChange)="onPageChange($event)"
      />
    }
  </section>
</div>
